// Every query and mutation of cloud's own screens (the account, sign-in, the
// waitlist, connected apps). Keys are written here only.
//
// Account things live under ["me", ...] like Me's own (cleared on sign out);
// what is not the person's (the sign-in options, the operator's waitlist, an
// OAuth client's public name) under ["cloud", ...].
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type Api, optimisticMutation, useApi } from "@trameria/ui-core";
import { addPasskey } from "./auth/client";
import { guessPasskeyName } from "./auth/passkey-name";

export interface AuthConfig {
  google: boolean;
  turnstileSiteKey: string | null;
  devAutoLogin: boolean;
}

export const authConfigQuery = (api: Api) =>
  queryOptions({
    queryKey: ["cloud", "auth-config"] as const,
    queryFn: () => api<AuthConfig>("/api/auth-config"),
    staleTime: Number.POSITIVE_INFINITY,
  });

export interface Account {
  userId: string;
  name: string;
  email: string | null;
  role: "member" | "admin";
  approved: boolean;
  passkeys: number;
  google: boolean;
}

export const accountKey = ["me", "account"] as const;

/** 401 is an answer here (signed out), not something to try again. */
export const accountQuery = (api: Api) =>
  queryOptions({
    queryKey: accountKey,
    queryFn: () => api<Account>("/api/me/account"),
    retry: false,
  });

// ---- passkeys (better-auth's passkey plugin) ----

export interface Passkey {
  id: string;
  name?: string | null;
  createdAt: string;
}

export const passkeysKey = ["me", "passkeys"] as const;

export const passkeysQuery = (api: Api) =>
  queryOptions({
    queryKey: passkeysKey,
    queryFn: () => api<Passkey[]>("/api/auth/passkey/list-user-passkeys"),
  });

/** WebAuthn runs in the browser, through the auth client. */
export function useAddPasskey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await addPasskey(guessPasskeyName());
      if (error) throw new Error(error.message ?? error.code ?? "passkey");
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: passkeysKey }),
        qc.invalidateQueries({ queryKey: accountKey }),
      ]),
  });
}

export function useDeletePasskey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api("/api/auth/passkey/delete-passkey", {
        method: "POST",
        body: { id },
      }),
    ...optimisticMutation<Passkey[], string>(qc, passkeysKey, (old, id) =>
      old?.filter((p) => p.id !== id),
    ),
  });
}

// ---- connected apps (MCP over OAuth) ----

export interface ConnectedApp {
  clientId: string;
  name: string | null;
  scopes: string[];
  grantedAt: string;
}

/**
 * A workspace the person is in, and whether connected apps reach it: they do
 * when the person allowed it (`allowed`) and its admins allow MCP
 * (`mcpAllowed`).
 */
export interface McpWorkspaceGrant {
  slug: string;
  name: string;
  allowed: boolean;
  mcpAllowed: boolean;
}

export interface McpAccount {
  serverUrl: string;
  apps: ConnectedApp[];
  workspaces: McpWorkspaceGrant[];
}

export const mcpKey = ["me", "mcp"] as const;

export const mcpQuery = (api: Api) =>
  queryOptions({
    queryKey: mcpKey,
    queryFn: () => api<McpAccount>("/api/me/mcp"),
  });

export function useDisconnectApp() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      api(`/api/me/mcp/apps/${encodeURIComponent(clientId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<McpAccount, string>(qc, mcpKey, (old, clientId) =>
      old
        ? { ...old, apps: old.apps.filter((a) => a.clientId !== clientId) }
        : old,
    ),
  });
}

/** Lets connected apps reach a workspace, or not (the person's own grant). */
export function useSetMcpWorkspace() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; name: string; allowed: boolean }) =>
      api(`/api/me/mcp/workspaces/${encodeURIComponent(input.slug)}`, {
        method: "PUT",
        body: { allowed: input.allowed },
      }),
    ...optimisticMutation<
      McpAccount,
      { slug: string; name: string; allowed: boolean }
    >(qc, mcpKey, (old, input) =>
      old
        ? {
            ...old,
            workspaces: old.workspaces.map((w) =>
              w.slug === input.slug ? { ...w, allowed: input.allowed } : w,
            ),
          }
        : old,
    ),
  });
}

// ---- OAuth consent ----

export interface OAuthClientPublic {
  client_id: string;
  client_name?: string;
}

export const oauthClientQuery = (api: Api, clientId: string) =>
  queryOptions({
    queryKey: ["cloud", "oauth-client", clientId] as const,
    queryFn: () =>
      api<OAuthClientPublic>(
        `/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
      ),
    // Without a name the page shows the client id; asking again changes nothing.
    retry: false,
  });

/** Answers the consent; resolves to where the client wants the browser to go. */
export function useOAuthConsent() {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: {
      accept: boolean;
      scope: string;
      oauthQuery: string;
    }) => {
      const res = await api<{ url?: string; redirect_uri?: string }>(
        "/api/auth/oauth2/consent",
        {
          method: "POST",
          body: {
            accept: input.accept,
            ...(input.accept && input.scope ? { scope: input.scope } : {}),
            oauth_query: input.oauthQuery,
          },
        },
      );
      const url = res.url ?? res.redirect_uri;
      if (!url) throw new Error("consent: no redirect");
      return url;
    },
  });
}

// ---- the operator's waitlist ----

export interface WaitlistEntry {
  userId: string;
  email: string;
  name: string;
  createdAt: string;
  via: "email" | "google";
}

export const waitlistKey = ["cloud", "admin", "waitlist"] as const;

export const waitlistQuery = (api: Api) =>
  queryOptions({
    queryKey: waitlistKey,
    queryFn: async () =>
      (await api<{ entries: WaitlistEntry[] }>("/api/admin/waitlist")).entries,
  });

export function useApprove() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api(`/api/admin/waitlist/${encodeURIComponent(userId)}/approve`, {
        method: "POST",
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: waitlistKey }),
  });
}
